<script setup lang="ts">
import { computed, ref } from "vue";
import { ChevronDown, ChevronUp, CircleAlert, Info, TriangleAlert } from "@lucide/vue";
import Badge from "@/components/Reusables/Badge/Badge.vue";
import Table from "@/components/Reusables/Table/Table.vue";

const props = defineProps<{
  heatedFloorArea: number | null;
  buildingTypeEnum: number | null;
}>();

const isOpen = ref(false);
const isNoFrameOpen = ref(false);

const RESIDENTIAL_TYPES = new Set([2, 3, 4, 259, 260, 261, 262, 277]);
const HOTEL_TYPES = new Set([269]);
const OFFICE_TYPES = new Set([264]);
const SCHOOL_TYPES = new Set([265]);
const RETAIL_TYPES = new Set([266]);

type TypologyRow = {
  label: string;
  subtitle: string;
  base: number;
  types: Set<number>;
};

const typologies: TypologyRow[] = [
  {
    label: "Residential buildings",
    subtitle: "Houses, apartments, student housing",
    base: 30.0,
    types: RESIDENTIAL_TYPES,
  },
  {
    label: "Hotels",
    subtitle: "",
    base: 30.0,
    types: HOTEL_TYPES,
  },
  {
    label: "Offices & Administration",
    subtitle: "",
    base: 41.0,
    types: OFFICE_TYPES,
  },
  {
    label: "Schools & Institutions",
    subtitle: "",
    base: 41.0,
    types: SCHOOL_TYPES,
  },
  {
    label: "Commerce & Retail",
    subtitle: "",
    base: 41.0,
    types: RETAIL_TYPES,
  },
];

const A = computed(() => {
  const area = props.heatedFloorArea;
  return typeof area === "number" && area > 0 ? area : null;
});

const matchingRowIndex = computed<number | null>(() => {
  // Number(), because the match below is `Set.has()`: SameValueZero, so it does not coerce, and a string
  // "3" would never match the numeric 3 in these typology sets. Every other reader of buildingTypeEnum
  // already defends itself this way at the point of use; this was the one that trusted the field's
  // declared type, and went silently blank whenever a select had written a string into it.
  const typeEnum = Number(props.buildingTypeEnum);
  if (props.buildingTypeEnum === null || props.buildingTypeEnum === undefined || Number.isNaN(typeEnum)) return null;
  const idx = typologies.findIndex((row) => row.types.has(typeEnum));
  return idx >= 0 ? idx : null;
});

function maxEnergyFormula(base: number): string {
  return `E = ${base.toFixed(1)} + (1000/A)`;
}

function maxEnergyComputed(base: number): string | null {
  if (A.value === null) return null;
  const result = base + 1000 / A.value;
  return `= ${result.toFixed(1)} kWh/m²/yr`;
}

const matchedMaxEnergy = computed<string | null>(() => {
  const idx = matchingRowIndex.value;
  if (idx === null) return null;
  const val = maxEnergyComputed(typologies[idx].base);
  if (val === null) return null;
  return val.replace("= ", "");
});
</script>

<template>
  <div class="col-span-12">
    <!-- Toggle button -->
    <button
      type="button"
      class="rounded-8 border-app-blue-200 bg-app-blue-50 hover:bg-app-blue-100 flex w-full items-center gap-2 border px-4 py-3 text-left transition-colors"
      :class="{ 'rounded-b-none border-b-0': isOpen }"
      @click="isOpen = !isOpen">
      <Info
        class="icon-sm text-app-blue-500 shrink-0"
        :stroke-width="1.5"
        aria-hidden="true" />
      <span class="t-small text-app-blue-900 flex-1 font-semibold"> Get more information about B6 – Operational energy use </span>
      <component
        :is="isOpen ? ChevronUp : ChevronDown"
        class="icon-xs text-app-blue-400 shrink-0"
        :stroke-width="1.5"
        aria-hidden="true" />
    </button>

    <!-- Expandable panel -->
    <div
      v-if="isOpen"
      class="rounded-b-8 border-app-blue-200 bg-app-blue-50 flex flex-col gap-4 border border-t-0 px-4 pt-3 pb-4">
      <!-- Intro text -->
      <p class="t-small text-app-blue-800">
        In module B6 (Operational Energy Use), the building's expected energy consumption during operation must be entered. This
        is based on results from the building's
        <strong>energy frame calculation</strong>, which is prepared to document compliance with the energy requirements in the
        <strong>Danish Building Regulations (BR18) §§ 259 and 260</strong>.
      </p>
      <p class="t-small text-app-blue-800">
        For the LCA calculation, enter the building's calculated energy requirement per m² per year from the energy frame
        calculation – the actual energy consumption for heat and electricity
        <strong>before primary energy factors</strong>.
      </p>

      <!-- Energy frame table -->
      <div class="rounded-8 border-app-blue-200 overflow-hidden border">
        <Table
          caption="Max energy requirement by building type"
          table-class="w-full t-small">
          <template #thead>
            <thead>
              <tr class="t-xs bg-app-blue-100 text-app-blue-900 text-left font-semibold">
                <th
                  scope="col"
                  class="px-3 py-2">
                  Building type
                </th>
                <th
                  scope="col"
                  class="px-3 py-2 text-right">
                  Max energy requirement (kWh/m²/yr)
                  <span
                    v-if="A === null"
                    class="t-xs text-app-blue-600 block font-normal">
                    Enter heated floor area for calculated value
                  </span>
                </th>
              </tr>
            </thead>
          </template>
          <template #tbody>
            <tbody>
              <tr
                v-for="(row, idx) in typologies"
                :key="row.label"
                class="border-app-blue-100 border-t transition-colors"
                :class="
                  matchingRowIndex === idx ? 'bg-app-blue-200 text-app-blue-900 font-semibold' : 'bg-slate-0 text-slate-700'
                ">
                <td class="px-3 py-2">
                  {{ row.label }}
                  <span
                    v-if="row.subtitle"
                    class="t-xs block font-normal text-slate-500">
                    {{ row.subtitle }}
                  </span>
                  <Badge
                    v-if="matchingRowIndex === idx"
                    variant="info"
                    class="ml-1">
                    Your building
                  </Badge>
                </td>
                <td class="t-small px-3 py-2 text-right font-mono">
                  <span class="block">{{ maxEnergyFormula(row.base) }}</span>
                </td>
              </tr>
            </tbody>
          </template>
        </Table>
      </div>

      <p class="t-xs text-app-blue-700">
        <strong>A</strong> = Heated floor area in m²
        <br />
        <strong>E</strong> = Energy requirement in kWh/m²/yr
      </p>

      <!-- Worst-case recommendation -->
      <div class="rounded-8 border-app-yellow-200 bg-app-yellow-50 border px-4 py-3">
        <p class="t-xs text-app-yellow-900 mb-1 font-semibold">
          <!-- inline: Tailwind's preflight makes svg display:block, which would break the text flow -->
          <TriangleAlert
            class="icon-xs mr-1 inline"
            :stroke-width="1.5"
            aria-hidden="true" />
          Energy frame not yet calculated? Conservative estimate:
        </p>
        <p class="t-xs text-app-yellow-800">
          Set the total energy frame value
          <span v-if="matchedMaxEnergy">
            (<strong>{{ matchedMaxEnergy }}</strong
            >)
          </span>
          as <strong>electricity consumption</strong> and set heat to <strong>0 kWh/m²/yr</strong>. Since electricity typically
          has a higher climate impact than heat, this provides a conservative upper limit for the operational energy's climate
          contribution.
        </p>
      </div>

      <!-- Buildings without an energy frame nested dropdown -->
      <div>
        <button
          type="button"
          class="rounded-8 border-app-blue-300 bg-app-blue-100 hover:bg-app-blue-200 flex w-full items-center gap-2 border px-3 py-2 text-left transition-colors"
          :class="{ 'rounded-b-none border-b-0': isNoFrameOpen }"
          @click="isNoFrameOpen = !isNoFrameOpen">
          <Info
            class="icon-xs text-app-blue-500 shrink-0"
            :stroke-width="1.5"
            aria-hidden="true" />
          <span class="t-xs text-app-blue-900 flex-1 font-semibold"> Buildings without an energy frame </span>
          <component
            :is="isNoFrameOpen ? ChevronUp : ChevronDown"
            class="icon-xs text-app-blue-400 shrink-0"
            :stroke-width="1.5"
            aria-hidden="true" />
        </button>
        <div
          v-if="isNoFrameOpen"
          class="rounded-b-8 border-app-blue-300 bg-app-blue-100 flex flex-col gap-3 border border-t-0 px-3 pt-2 pb-3">
          <!-- Holiday homes section -->
          <p class="t-caption text-app-blue-800">
            For <strong>holiday homes, camping cabins and similar holiday residences</strong> - Operational energy climate impact
            (module B6) is based on standard energy demand per m² of heated floor area, adjusted to the building's energy source.
            For multiple sources, an area-weighted average is used.
          </p>
          <!-- Table 12 -->
          <div class="border-app-blue-200 overflow-hidden rounded-md border">
            <div class="bg-app-blue-700 dark:bg-app-blue-fill px-3 py-2">
              <p class="t-caption-bold text-white">
                Table 12 – Default values for energy consumption in holiday homes, camping cabins and similar holiday residences
              </p>
            </div>
            <Table caption="Table 12 – Energy consumption for holiday homes and camping cabins">
              <template #thead>
                <thead>
                  <tr class="bg-app-blue-100 text-left">
                    <th
                      scope="col"
                      class="t-caption-bold text-app-blue-900 px-3 py-2">
                      Energy consumption [kWh/m²]
                    </th>
                    <th
                      scope="col"
                      class="t-caption-bold text-app-blue-900 px-3 py-2 text-right">
                      Heat demand (electric heating, district heating or other heating)
                    </th>
                    <th
                      scope="col"
                      class="t-caption-bold text-app-blue-900 px-3 py-2 text-right">
                      Heat pump air/air – electricity demand
                    </th>
                    <th
                      scope="col"
                      class="t-caption-bold text-app-blue-900 px-3 py-2 text-right">
                      Heat pump air/water – electricity demand
                    </th>
                  </tr>
                </thead>
              </template>
              <template #tbody>
                <tbody>
                  <tr class="bg-slate-0 border-app-blue-100 border-t text-slate-700">
                    <td class="t-caption px-3 py-2"></td>
                    <td class="t-caption px-3 py-2 text-right font-mono">33.2 kWh/m²</td>
                    <td class="t-caption px-3 py-2 text-right font-mono">21.2 kWh/m²</td>
                    <td class="t-caption px-3 py-2 text-right font-mono">10.1 kWh/m²</td>
                  </tr>
                </tbody>
              </template>
            </Table>
          </div>

          <!-- Extensions section -->
          <p class="t-caption text-app-blue-800">
            For <strong>extensions</strong> - Operational energy climate impact (module B6) is based on standard energy demand per
            m² of heated floor area, depending on the extension's use. For mixed use, an area-weighted average is applied.
          </p>
          <!-- Table 13 -->
          <div class="border-app-blue-200 overflow-hidden rounded-md border">
            <div class="bg-app-blue-700 dark:bg-app-blue-fill px-3 py-2">
              <p class="t-caption-bold text-white">Table 13 – Default values for energy consumption in extensions</p>
            </div>
            <Table caption="Table 13 – Energy consumption for extensions">
              <template #thead>
                <thead>
                  <tr class="bg-app-blue-100 text-left">
                    <th
                      scope="col"
                      class="t-caption-bold text-app-blue-900 px-3 py-2">
                      Energy consumption [kWh/m²]
                    </th>
                    <th
                      scope="col"
                      class="t-caption-bold text-app-blue-900 px-3 py-2 text-right">
                      Heat demand
                    </th>
                    <th
                      scope="col"
                      class="t-caption-bold text-app-blue-900 px-3 py-2 text-right">
                      Electricity demand
                    </th>
                  </tr>
                </thead>
              </template>
              <template #tbody>
                <tbody>
                  <tr class="bg-slate-0 border-app-blue-100 border-t text-slate-700">
                    <td class="t-caption px-3 py-2">Single-family houses</td>
                    <td class="t-caption px-3 py-2 text-right font-mono">37 kWh/m²</td>
                    <td class="t-caption px-3 py-2 text-right font-mono">2 kWh/m²</td>
                  </tr>
                  <tr class="bg-slate-0 border-app-blue-100 border-t text-slate-700">
                    <td class="t-caption px-3 py-2">Other residential</td>
                    <td class="t-caption px-3 py-2 text-right font-mono">32 kWh/m²</td>
                    <td class="t-caption px-3 py-2 text-right font-mono">2 kWh/m²</td>
                  </tr>
                  <tr class="bg-slate-0 border-app-blue-100 border-t text-slate-700">
                    <td class="t-caption px-3 py-2">Other</td>
                    <td class="t-caption px-3 py-2 text-right font-mono">32 kWh/m²</td>
                    <td class="t-caption px-3 py-2 text-right font-mono">7 kWh/m²</td>
                  </tr>
                </tbody>
              </template>
            </Table>
          </div>
        </div>
      </div>

      <!-- Disclaimer -->
      <p class="t-xs text-app-blue-700">
        <!-- inline: Tailwind's preflight makes svg display:block, which would break the text flow -->
        <CircleAlert
          class="icon-xs mr-1 inline"
          :stroke-width="1.5"
          aria-hidden="true" />
        <strong>Note:</strong> The above is only for use in the early design phase. When the energy frame calculation has been
        completed (e.g. in Be18), the values must be updated with the actual calculated energy requirements for heat and
        electricity before project completion.
      </p>
    </div>
  </div>
</template>
